import type { FirestoreTimestamp, SharedPost, CommunityPost, UserProfile } from "./types";

// ── HTML Escaping ──────────────────────────────────────────────────────────
export function escapeHtml(input = ""): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// ── Dates ──────────────────────────────────────────────────────────────────
export function timestampToDate(raw: FirestoreTimestamp): Date {
  if (!raw) return new Date();
  if (typeof raw === "object" && "toDate" in raw) return (raw as { toDate(): Date }).toDate();
  if (raw instanceof Date) return raw;
  if (typeof raw === "number") return new Date(raw);
  return new Date();
}

export function nowIsoDateInTimezone(timezone: string): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(new Date());
  const year = parts.find((p) => p.type === "year")?.value ?? "1970";
  const month = parts.find((p) => p.type === "month")?.value ?? "01";
  const day = parts.find((p) => p.type === "day")?.value ?? "01";
  return `${year}-${month}-${day}`;
}

export function toRelativeDate(rawDate: FirestoreTimestamp): string {
  const date = timestampToDate(rawDate);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return `${Math.floor(seconds / 604800)}w`;
}

export function toLocalDatetimeInput(dateRaw: FirestoreTimestamp): string {
  const date = timestampToDate(dateRaw);
  const pad = (v: number) => String(v).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// ── IDs ────────────────────────────────────────────────────────────────────
export function makeId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") return crypto.randomUUID().toUpperCase();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`.toUpperCase();
}

// ── String utils ───────────────────────────────────────────────────────────
export function normalizeJoinCode(input = ""): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export function shortUid(uid = ""): string {
  return `@${uid.slice(0, 8)}`;
}

// ── Profile display ────────────────────────────────────────────────────────
export function displayNameForProfile(profile: UserProfile | null): string {
  if (!profile) return "";
  return profile.profile?.displayName || profile.username || "LOT user";
}

// ── Post display ───────────────────────────────────────────────────────────
export function displayAuthor(
  post: SharedPost | CommunityPost,
  usernameCache: Map<string, string>,
  currentUid: string | undefined
): string {
  if (post.attribution === "username") {
    if (post.authorUsername) return post.authorUsername;
    if (usernameCache.get(post.authorUid)) return usernameCache.get(post.authorUid)!;
    return shortUid(post.authorUid);
  }
  return "Anonymous";
}

// ── Scoring ────────────────────────────────────────────────────────────────
export function activeCommunityScore(post: CommunityPost): number {
  const ageHours = Math.max(
    0,
    (Date.now() - timestampToDate(post.createdAt).getTime()) / 3600000
  );
  const recency = 1 / Math.pow(ageHours + 2, 1.15);
  const base = Number(post.heatScore || 0) * 0.6 + Number(post.replyRate || 0) * 0.4;
  const conversationBoost = Number(post.replyCount || 0) > 0 ? (ageHours <= 72 ? 1.35 : 1.1) : 1;
  const agePenalty = ageHours > 336 ? 0.6 : 1;
  return (base + recency * 3) * conversationBoost * agePenalty;
}

export function rankFeed(posts: SharedPost[]): SharedPost[] {
  return [...posts].sort((a, b) => {
    const aScore = Number(a.heatScore || 0) * 0.6 + Number(a.replyRate || 0) * 0.4;
    const bScore = Number(b.heatScore || 0) * 0.6 + Number(b.replyRate || 0) * 0.4;
    if (aScore === bScore) {
      return timestampToDate(b.sharedAt).getTime() - timestampToDate(a.sharedAt).getTime();
    }
    return bScore - aScore;
  });
}

// ── Deduplication ──────────────────────────────────────────────────────────
export function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export function dedupeIds(ids: string[]): string[] {
  const seen = new Set<string>();
  return (ids || []).filter((id) => {
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function chunk<T>(items: T[], size: number): T[][] {
  const output: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    output.push(items.slice(i, i + size));
  }
  return output;
}

// ── Toast ──────────────────────────────────────────────────────────────────
export function showToast(message: string, type?: "error" | "success"): void {
  const toastRoot = document.getElementById("toast-root");
  if (!toastRoot) return;
  const el = document.createElement("div");
  el.className = type ? `toast toast-${type}` : "toast";
  el.textContent = message;
  toastRoot.appendChild(el);
  // Errors linger a little longer so they are never missed.
  const holdMs = type === "error" ? 3600 : 2100;
  setTimeout(() => {
    el.style.opacity = "0";
    el.style.transform = "translateY(8px)";
  }, holdMs);
  setTimeout(() => el.remove(), holdMs + 500);
}

// ── DOM ────────────────────────────────────────────────────────────────────
export function getFieldValue(selector: string, fallback = ""): string {
  const el = document.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(selector);
  if (!el) return fallback;
  return el.value ?? fallback;
}
